import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Minus, Plus, ArrowRight, Loader2, Lock, CreditCard } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { MARKETING } from "@/lib/marketingContent";
import { quote, money } from "@/lib/pricing";
import { PlanGapDialog } from "@/components/PlanGap";

const Tick = ({ on }) =>
  on ? <Check size={16} strokeWidth={2} className="text-[color:var(--marketing-primary)]" aria-label="Included" />
    : <Minus size={16} strokeWidth={1.5} className="text-slate-300" aria-label="Not included" />;

export const PricingTable = ({ showMatrix = true }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [cat, setCat] = useState(null);
  const [annual, setAnnual] = useState(false);
  const [seats, setSeats] = useState(1);

  useEffect(() => {
    api.get("/public/plans").then(({ data }) => setCat(data))
      .catch(() => toast.error("Could not load pricing"));
  }, []);

  if (!cat) return <p className="py-16 text-center text-sm text-slate-400">Loading plans…</p>;

  const cap = cat.max_self_serve_seats || 99;
  const overCap = seats > cap;

  // Straight to purchase. Signed in, we go to billing and let it open Stripe; signed out, the intent
  // rides through signup so the customer lands on checkout instead of having to hunt for it.
  // The session must come from the auth context, not from the presence of a token: a stale token
  // sent buyers to a protected page, which bounced them to /login and lost the purchase entirely.
  const buyNow = (plan, planSeats) => {
    const query = `plan=${plan}&interval=${annual ? "annual" : "monthly"}&seats=${planSeats}&buy=1`;
    navigate(user ? `/admin/billing?${query}` : `/signup?${query}`);
  };

  return (
    <div data-testid="pricing-table">
      <div className="flex justify-center">
        <div className="inline-flex rounded-sm border border-[color:var(--marketing-border)] bg-white p-1" data-testid="pricing-cycle-toggle">
          {[["Monthly", false], ["Annual — save 17%", true]].map(([label, val]) => (
            <button key={label} data-testid={`pricing-cycle-${val ? "annual" : "monthly"}`}
              onClick={() => setAnnual(val)}
              className={`px-5 py-2 text-sm font-semibold transition-colors ${annual === val ? "bg-slate-900 text-white" : "text-[color:var(--marketing-text-muted)] hover:text-slate-900"}`}>
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-8 flex flex-wrap items-center justify-center gap-x-5 gap-y-3">
        <div className="flex items-center gap-2.5" data-testid="pricing-seat-picker">
          <span className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">
            How many people?
          </span>
          <div className="flex items-center border border-[color:var(--marketing-border)] bg-white">
            <button
              type="button"
              data-testid="pricing-seats-minus"
              aria-label="Fewer seats"
              onClick={() => setSeats((s) => Math.max(1, s - 1))}
              className="px-3 py-2 text-slate-500 transition-colors hover:text-slate-900"
            >
              <Minus size={13} strokeWidth={2} />
            </button>
            <input
              data-testid="pricing-seats-input"
              aria-label="Number of seats"
              value={seats}
              onChange={(e) => {
                const next = parseInt(e.target.value.replace(/\D/g, ""), 10);
                setSeats(Number.isNaN(next) ? 1 : Math.min(500, Math.max(1, next)));
              }}              className="w-12 border-x border-[color:var(--marketing-border)] py-2 text-center text-sm font-semibold tabular-nums outline-none"
            />
            <button
              type="button"
              data-testid="pricing-seats-plus"
              aria-label="More seats"
              onClick={() => setSeats((s) => Math.min(500, s + 1))}
              className="px-3 py-2 text-slate-500 transition-colors hover:text-slate-900"
            >
              <Plus size={13} strokeWidth={2} />
            </button>
          </div>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-3">
        {cat.plans.map((p) => {
          const pro = p.recommended;
          const price = annual ? p.price_annual : p.price_monthly;
          const q = p.quote_only ? null : quote(p, annual, seats);
          const planSeats = q ? q.seats : Math.max(seats, p.min_seats || 1);
          // The upgrade decision, put where the decision is actually made.
          const upgrade = cat.plans.find((x) => x.recommended && x.key !== p.key);
          return (
            <div key={p.key} data-testid={`pricing-card-${p.key}`}
              className={`flex flex-col border p-8 ${pro ? "border-slate-900 bg-slate-900 text-white lg:-mt-4 lg:mb-[-1rem]" : "border-[color:var(--marketing-border)] bg-white"}`}>
              {pro && (
                <span data-testid="pricing-recommended-badge"
                  className="mb-4 self-start bg-[color:var(--marketing-primary)] px-2 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-white">
                  Recommended for PMOs
                </span>
              )}
              <h3 className={`font-marketing-display text-2xl font-bold tracking-tight ${pro ? "text-white" : "text-[color:var(--marketing-text)]"}`}>
                {p.name}
              </h3>
              <p className={`mt-2 text-sm ${pro ? "text-slate-300" : "text-[color:var(--marketing-text-muted)]"}`}>{p.tagline}</p>

              <div className="mt-7 flex items-end gap-1.5">
                <span data-testid={`pricing-price-${p.key}`} className="font-marketing-display text-5xl font-black tracking-tight">
                  {p.quote_only ? "POA" : `${cat.currency_symbol}${price}`}
                </span>
                {!p.quote_only && (
                  <span className={`pb-2 text-sm ${pro ? "text-slate-400" : "text-slate-500"}`}>/user/month</span>
                )}
              </div>
              <p className={`mt-1 text-xs ${pro ? "text-slate-400" : "text-slate-500"}`}>
                {p.quote_only
                  ? `Quoted to your requirements · from ${p.min_seats} seats`
                  : annual ? "Billed annually · from a single user"
                    : "Billed monthly · from a single user"}
              </p>

              {!p.quote_only && (
                <div data-testid={`pricing-total-${p.key}`}
                  className={`mt-4 border-t pt-3 text-sm ${pro ? "border-slate-700 text-white" : "border-slate-100 text-[color:var(--marketing-text)]"}`}>
                  <p>
                    <strong className="font-semibold">{money(q.total)}</strong>{" "}
                    <span className={pro ? "text-slate-400" : "text-slate-500"}>
                      {annual ? "per year" : "per month"} for {planSeats} {planSeats === 1 ? "person" : "people"}
                      {planSeats > seats ? " (this plan's minimum)" : ""}
                    </span>
                  </p>
                  {q.discountPct > 0 && (
                    <p data-testid={`pricing-saving-${p.key}`}
                      className={`mt-1.5 inline-block px-2 py-0.5 text-xs font-semibold ${
                        pro ? "bg-[color:var(--marketing-primary)] text-white" : "bg-emerald-50 text-emerald-800"}`}>
                      {q.discountPct}% volume discount — you save {money(q.saving)}
                      {annual ? " a year" : " a month"}
                    </p>
                  )}
                  {!annual && (p.volume_tiers_monthly || []).length > 0 && q.discountPct === 0 && (
                    <p className={`mt-1.5 text-xs ${pro ? "text-slate-400" : "text-slate-500"}`}>
                      15% off from 25 people, 25% off from 100.
                    </p>
                  )}
                </div>
              )}

              <button
                data-testid={`pricing-cta-${p.key}`}
                onClick={() => {
                  if (p.quote_only) {
                    document.getElementById("enquiry")?.scrollIntoView({ behavior: "smooth", block: "start" });
                    if (!document.getElementById("enquiry")) navigate("/pricing#enquiry");
                  } else {
                    navigate(`/signup?plan=${p.key}&interval=${annual ? "annual" : "monthly"}&seats=${planSeats}`);
                  }
                }}
                className={`mt-7 inline-flex items-center justify-center gap-2 rounded-sm px-5 py-3 text-sm font-semibold transition-colors ${
                  pro ? "bg-white text-slate-900 hover:bg-slate-200"
                    : "bg-[color:var(--marketing-primary)] text-white hover:bg-[color:var(--marketing-primary-hover)]"}`}>
                {p.cta} <ArrowRight size={15} strokeWidth={1.5} />
              </button>

              {!p.quote_only && (
                <button
                  data-testid={`pricing-buy-${p.key}`}
                  onClick={() => (overCap
                    ? document.getElementById("enquiry")?.scrollIntoView({ behavior: "smooth", block: "start" })
                    : buyNow(p.key, planSeats))}
                  className={`mt-2.5 inline-flex items-center justify-center gap-2 rounded-sm border-2 px-5 py-2.5 text-sm font-semibold transition-colors ${
                    pro ? "border-slate-600 text-white hover:border-white"
                      : "border-slate-200 text-[color:var(--marketing-text)] hover:border-slate-900"}`}>
                  {overCap ? <>Talk to us about {planSeats} seats</> : <><CreditCard size={14} strokeWidth={1.5} /> Buy now</>}
                </button>
              )}

              {upgrade && !p.quote_only && (
                <PlanGapDialog
                  matrix={cat.matrix}
                  from={p}
                  to={upgrade}
                  dark={pro}
                  onUpgrade={() => buyNow(upgrade.key, Math.max(seats, upgrade.min_seats || 1))}
                />
              )}

              <p className={`mt-6 text-xs font-semibold uppercase tracking-[0.15em] ${pro ? "text-slate-400" : "text-slate-400"}`}>
                {p.key === "foundation" ? "Includes" : "Everything before, plus"}
              </p>              <ul className="mt-3 space-y-2.5">
                {p.headline_points.map((pt) => (
                  <li key={pt} className={`flex gap-2.5 text-sm ${pro ? "text-slate-200" : "text-[color:var(--marketing-text-muted)]"}`}>
                    <Check size={15} strokeWidth={2} className={pro ? "mt-0.5 shrink-0 text-white" : "mt-0.5 shrink-0 text-[color:var(--marketing-primary)]"} />
                    {pt}
                  </li>
                ))}
              </ul>
              <p className={`mt-6 border-t pt-4 text-xs ${pro ? "border-slate-700 text-slate-400" : "border-slate-100 text-slate-500"}`}>
                {p.best_for}
              </p>
            </div>
          );
        })}
      </div>

      <p className="mt-6 text-center text-xs text-slate-500">
        {cat.vat_note} {cat.trial_days}-day free trial of {cat.plans.find((p) => p.key === cat.trial_plan)?.name} features. No card required.
        {" "}Prefer to skip the trial? <strong className="font-semibold">Buy now</strong> takes the seat count above straight to checkout.
        {overCap && (
          <>
            {" "}
            <strong className="font-semibold text-slate-700">
              Above {cap} people we quote directly, which gives you volume pricing and an
              assurance pack for your IT function.
            </strong>
          </>
        )}
      </p>

      {showMatrix && (
        <div className="mt-20 overflow-x-auto" data-testid="pricing-matrix">
          <h3 className="font-marketing-display text-2xl font-bold tracking-tight">The tiers in full</h3>
          <table className="mt-6 w-full min-w-[760px] border-collapse text-sm">
            <thead>
              <tr className="border-b-2 border-slate-900">
                <th className="py-3 text-left font-semibold">Capability</th>
                {cat.plans.map((p) => (
                  <th key={p.key} className="w-32 py-3 text-center font-semibold">{p.name}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {cat.matrix.map((group) => (
                <React.Fragment key={group.group}>
                  <tr className="bg-slate-50">
                    <td colSpan={4} className="px-1 py-2.5 text-xs font-bold uppercase tracking-[0.15em] text-slate-500">
                      {group.group}
                    </td>
                  </tr>
                  {group.rows.map((row) => (
                    <tr key={row.key} data-testid={`matrix-row-${row.key}`} className="border-b border-slate-100">
                      <td className="py-3 pr-4 text-[color:var(--marketing-text-muted)]">{row.label}</td>
                      {cat.plans.map((p) => (
                        <td key={p.key} className="py-3 text-center">
                          <span className="inline-flex justify-center"><Tick on={row.values[p.key]} /></span>
                        </td>
                      ))}
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export const EnterpriseEnquiry = ({
  heading = "Speak to us about Enterprise",
  blurb = "Tell us about your governance and data residency requirements, and we will come back with a quote, an assurance pack for your IT function and a route to purchase.",
}) => {
  const [form, setForm] = useState({ name: "", email: "", organisation: "", seats: "", residency: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/public/enterprise-enquiry", form);
      setDone(true);
      toast.success(data.message);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Could not send your enquiry");
    } finally {
      setBusy(false);
    }
  };

  if (done)
    return (
      <div data-testid="enquiry-success" className="border border-[color:var(--marketing-border)] bg-white p-8">
        <h3 className="font-marketing-display text-xl font-bold">Thank you — enquiry received</h3>
        <p className="mt-2 text-sm text-[color:var(--marketing-text-muted)]">
          We will be in touch within one working day. If it is urgent, email{" "}
          <a className="font-semibold text-[color:var(--marketing-primary)]" href={`mailto:${MARKETING.salesEmail}`}>
            {MARKETING.salesEmail}
          </a>.
        </p>
      </div>
    );

  return (
    <form onSubmit={submit} data-testid="enterprise-enquiry-form" className="border border-[color:var(--marketing-border)] bg-white p-8">
      <h3 className="font-marketing-display text-2xl font-bold tracking-tight">{heading}</h3>
      <p className="mt-2 max-w-xl text-sm text-[color:var(--marketing-text-muted)]">{blurb}</p>
      <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
        <label className="block">
          <span className="overline-label">Your name</span>
          <Input data-testid="enquiry-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </label>
        <label className="block">
          <span className="overline-label">Work email</span>
          <Input data-testid="enquiry-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        </label>
        <label className="block">
          <span className="overline-label">Organisation</span>
          <Input data-testid="enquiry-org" required value={form.organisation} onChange={(e) => setForm({ ...form, organisation: e.target.value })} />
        </label>
        <label className="block">
          <span className="overline-label">Approximate seats</span>
          <Input data-testid="enquiry-seats" placeholder="e.g. 60" value={form.seats} onChange={(e) => setForm({ ...form, seats: e.target.value })} />
        </label>
        <label className="block md:col-span-2">
          <span className="overline-label">Data residency or assurance requirements</span>
          <Input data-testid="enquiry-residency" placeholder="e.g. UK South only, Cyber Essentials Plus, DPIA required"
            value={form.residency} onChange={(e) => setForm({ ...form, residency: e.target.value })} />
        </label>
        <label className="block md:col-span-2">
          <span className="overline-label">Anything else</span>
          <Textarea data-testid="enquiry-message" rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
        </label>
      </div>
      <button data-testid="enquiry-submit" disabled={busy}
        className="mt-6 inline-flex items-center gap-2 rounded-sm bg-[color:var(--marketing-primary)] px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-[color:var(--marketing-primary-hover)] disabled:opacity-60">
        {busy ? <Loader2 size={15} className="animate-spin" /> : <ArrowRight size={15} strokeWidth={1.5} />} Send enquiry
      </button>
      <p className="mt-3 flex items-center gap-1.5 text-xs text-slate-500">
        <Lock size={11} strokeWidth={1.5} /> We use your details only to respond to this enquiry.
      </p>
    </form>
  );
};
